import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from '@material-ui/core';
import HistoryIcon from '@material-ui/icons/History';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import { Actions as Common } from '../../../store/ducks/common';
import { deleteSession, listSessions, readSession } from '../../../common/sessionHistory';
import { formatLocaleDate } from '../../../common/utils';
import { useErrorNotice } from '../../../hooks/useErrorNotice';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the history icon and the
 * list of saved analyses behind it.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  icon: {
    marginRight: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(3, 1),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  module: {
    textTransform: 'capitalize',
  },
}));

/**
 * @function SessionHistoryButton
 * @description Lists the analyses this browser has saved and reopens one where it left off.
 * An analysis has always been recoverable - the export writes a file and the import reads it back -
 * but only by finding that file again, which in practice meant a folder of downloads named after
 * the module and nothing else. Every export and every import now also files the analysis here,
 * under where it is and the years it covers, so coming back to one is a click.
 * The file itself is untouched by any of this: deleting an entry removes this copy, not the
 * download.
 * @param {Object} props
 * @param {String} [props.module] - Shows only this module's analyses. Omitted on the module
 *  selection page, where all of them are worth listing.
 * @param {Function} props.onOpen - Hands the chosen analysis to whoever knows how to restore it.
 * **Called by:
 * @see {NavigationBar} - inside a module.
 * @see {SelectionSessionImportButton} - on the module selection page.
 * @memberof module:Core
 * @returns {JSX.Element|null} The icon and its dialog, or nothing while there is no history.
 */
const SessionHistoryButton = ({ module, onOpen }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const notifyError = useErrorNotice();

  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);

  const refresh = useCallback(async () => {
    const saved = await listSessions();
    setEntries(module ? saved.filter((entry) => entry.module === module) : saved);
  }, [module]);

  /**
   * The icon is only worth showing when there is something behind it, so the list is read once on
   * mount as well as whenever the dialog opens.
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpen = async (entry) => {
    setOpen(false);
    dispatch(Common.notice(t('forms.core.sessionHistory.opening'), entry.title));
    try {
      const session = await readSession(entry.id);
      if (!session) {
        notifyError(t('forms.core.sessionHistory.missing'));
        return;
      }
      await onOpen(session);
    } catch (error) {
      console.error('[SessionHistory] the analysis could not be reopened:', error);
      notifyError(error, { fallback: t('forms.acquisition.sessionImport.error') });
    } finally {
      dispatch(Common.clearNotice());
    }
  };

  const handleDelete = async (entry) => {
    await deleteSession(entry.id);
    refresh();
  };

  if (!entries.length) return null;

  return (
    <React.Fragment>
      <Tooltip title={t('forms.core.sessionHistory.button')}>
        <IconButton
          size="medium"
          className={classes.icon}
          onClick={() => {
            refresh();
            setOpen(true);
          }}>
          <HistoryIcon fontSize="inherit" color="primary" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('forms.core.sessionHistory.title')}</DialogTitle>
        <DialogContent dividers>
          {entries.length === 0 ? (
            <Typography className={classes.empty}>{t('forms.core.sessionHistory.empty')}</Typography>
          ) : (
            <List>
              {entries.map((entry) => (
                <ListItem button key={entry.id} onClick={() => handleOpen(entry)}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="baseline" style={{ gap: 8 }}>
                        <Typography variant="body1">{entry.title || t('forms.core.sessionHistory.unnamed')}</Typography>
                        {entry.detail && (
                          <Typography variant="body2" color="textSecondary">
                            {entry.detail}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary" className={classes.module}>
                        {entry.module} · {formatLocaleDate(new Date(entry.savedAt).toISOString())}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={t('forms.core.sessionHistory.remove')}>
                      <IconButton edge="end" size="small" onClick={() => handleDelete(entry)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setOpen(false)}>
            {t('forms.core.sessionHistory.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default SessionHistoryButton;
